/**
 * What the network is doing, on screen.
 *
 * Two problems, one file.
 *
 * **A lockstep stall is silent.** A client without the committed set for its
 * next tick is *supposed* to wait — that is the whole design, and guessing
 * instead is a desync — but waiting looks exactly like a crash. The lobby
 * already reported it, and the lobby is hidden once the match starts, so during
 * play the game simply froze: construction stopped mid-building, nothing moved,
 * no explanation anywhere.
 *
 * **There was no way to find out why.** A player reporting "multiplayer feels
 * finicky" cannot tell a stall from a drop from a desync from a dead socket,
 * and neither could anyone reading the report. F3 opens a panel with the tick
 * each side has reached and a log of every relay event, which turns "it broke"
 * into something with a timestamp.
 */

export interface NetDiagnostics {
  slot: number
  tick: number
  committedThrough: number
  submittedThrough: number
  stalling: boolean
  sinceCommitMs: number
  closed: boolean
}

export interface NetSource {
  diagnostics(): NetDiagnostics
}

/** Entries older than this drop off the log. */
const LOG_LIMIT = 40

export class NetStatus {
  private readonly banner: HTMLDivElement
  private readonly panel: HTMLDivElement
  private readonly body: HTMLPreElement
  private readonly log: string[] = []
  private source: NetSource | null = null
  private open = false
  private startedAt = Date.now()

  constructor(root: HTMLElement) {
    this.banner = document.createElement('div')
    this.banner.className = 'ba-netbanner'

    this.panel = document.createElement('div')
    this.panel.className = 'ba-netpanel'
    this.body = document.createElement('pre')
    this.panel.append(this.body)

    root.append(this.banner, this.panel)
  }

  attach(source: NetSource): void {
    this.source = source
    this.startedAt = Date.now()
    this.note('match started')
  }

  /** Record a relay event. Everything the relay reports lands here. */
  note(text: string): void {
    const at = ((Date.now() - this.startedAt) / 1000).toFixed(1)
    this.log.push(`${at.padStart(7)}s  ${text}`)
    if (this.log.length > LOG_LIMIT) this.log.shift()
  }

  /**
   * The one message that must interrupt play. Everything else goes to the log;
   * a stall stops the game, so it says so over the top of it.
   */
  setBanner(text: string, bad = false): void {
    this.banner.textContent = text
    this.banner.classList.toggle('visible', text !== '')
    this.banner.classList.toggle('bad', bad)
  }

  toggle(): void {
    this.open = !this.open
    this.panel.classList.toggle('visible', this.open)
  }

  /** Called every frame. Cheap enough to leave running while closed. */
  update(): void {
    if (!this.open || !this.source) return
    const d = this.source.diagnostics()
    const behind = d.committedThrough - d.tick
    this.body.textContent = [
      `slot            ${d.slot}`,
      `tick            ${d.tick}`,
      `committed to    ${d.committedThrough}  (${behind >= 0 ? '+' : ''}${behind})`,
      `submitted to    ${d.submittedThrough}`,
      `last commit     ${d.sinceCommitMs < 0 ? 'never' : `${d.sinceCommitMs} ms ago`}`,
      `state           ${d.closed ? 'CLOSED' : d.stalling ? 'STALLED' : 'running'}`,
      '',
      ...this.log,
    ].join('\n')
  }
}
